import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  AddressField,
  addressDraftFrom,
  addressIsUsable,
  emptyAddress,
  type AddressDraft,
} from '../components/AddressField.js'
import { Icon } from '../components/Icon.js'
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardHeader,
  CommandBar,
  CommandTitle,
  ErrorState,
  Field,
  Input,
  Page,
  Select,
  SkeletonRows,
  Spinner,
  Tabs,
  humanise,
  useOnNavy,
} from '../components/ui.js'
import { BRAND } from '../lib/brand.js'
import { cn } from '../lib/cn.js'
import { formatDateTime, money, relativeTime } from '../lib/format.js'
import { useCurrentUser, useSession } from '../lib/session.js'
import { errorMessage, fieldErrors, useTRPC } from '../lib/trpc.js'
import { Certifications } from './person/Certifications.js'

/**
 * One person, as a page.
 *
 * This replaced a modal. AGENTS rule 13 — a job has no page — is about jobs
 * specifically: a job is glanced at over whatever you were doing, which is why
 * it is an overlay on `?jobId=`. A person is a record you sit with, and the
 * certifications tab in particular is a form with photographs in it. A dialog is
 * the wrong container for that, and it cannot be linked to from an expiry
 * report.
 */

type Tab = 'details' | 'certifications' | 'schedule'

const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: 'details', label: 'Details' },
  { key: 'certifications', label: 'Certifications' },
  { key: 'schedule', label: 'Schedule' },
]

export function PersonDetail() {
  const { userId } = useParams<{ userId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { can } = useSession()

  const requested = searchParams.get('tab')
  const tab: Tab = TABS.find((entry) => entry.key === requested)?.key ?? 'details'

  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams)
    if (next === 'details') params.delete('tab')
    else params.set('tab', next)
    setSearchParams(params, { replace: true })
  }

  const trpc = useTRPC()
  const detail = useQuery({
    ...trpc.users.get.queryOptions({ id: userId ?? '' }),
    enabled: Boolean(userId),
  })

  if (!userId) return <ErrorState message="No person in that address." />

  return (
    <Page width="xwide" field>
      <Card variant="board">
        {/* The job card's shape: a navy header for the record, its tabs under it. */}
        <CommandBar className="block rounded-none px-0 py-0">
          <div className="px-[var(--row-pad-x)] pt-3 pb-2">
            <Link
              to="/settings?tab=people"
              className="inline-flex items-center gap-1 text-xs font-medium text-ink-500 hover:text-ink-900 desk:text-sky/80 desk:hover:text-white"
            >
              <Icon name="chevronDown" className="size-3 rotate-90" strokeWidth={2.25} />
              All people
            </Link>
          </div>

          {detail.data ? <PersonHeader user={detail.data} /> : null}
        </CommandBar>

        {detail.isPending ? (
          <div className="grid place-items-center py-16">
            <Spinner className="size-5 text-ink-400" />
          </div>
        ) : detail.error || !detail.data ? (
          <ErrorState
            message={detail.error ? errorMessage(detail.error) : 'That person was not found.'}
          />
        ) : null}

        <Tabs
          label="Person"
          value={tab}
          onChange={setTab}
          options={TABS.map((entry) => ({ value: entry.key, label: entry.label }))}
        />
      </Card>

      {tab === 'details' ? <DetailsTab userId={userId} /> : null}
      {tab === 'certifications' ? (
        <Certifications userId={userId} canEdit={can('USER_MANAGE')} />
      ) : null}
      {tab === 'schedule' ? <ScheduleTab userId={userId} /> : null}
    </Page>
  )
}

/* -------------------------------------------------------------------------- */

type UserDetail = NonNullable<ReturnType<typeof useUserDetail>>

function useUserDetail(userId: string) {
  const trpc = useTRPC()
  return useQuery(trpc.users.get.queryOptions({ id: userId })).data
}

function PersonHeader({ user }: { user: NonNullable<UserDetail> }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const me = useCurrentUser()
  const { can } = useSession()

  const invalidate = () => void queryClient.invalidateQueries(trpc.users.pathFilter())

  const archive = useMutation(trpc.users.archive.mutationOptions({ onSuccess: invalidate }))
  const restore = useMutation(trpc.users.restore.mutationOptions({ onSuccess: invalidate }))
  const resend = useMutation(trpc.users.resendInvite.mutationOptions())

  const pending = user._count.credentials === 0
  const onNavy = useOnNavy()

  return (
    <div className="flex flex-wrap items-start gap-3 px-[var(--row-pad-x)] pb-3">
      <Avatar name={user.name} colour={user.colour} size="lg" className={cn(onNavy && 'ring-2 ring-white/20')} />

      <div className="min-w-0 flex-1">
        <CommandTitle className="truncate">{user.name}</CommandTitle>
        <p className={cn('truncate', onNavy ? 'text-[13px] font-medium text-sky' : 'text-xs text-ink-500')}>
          {[
            user.role.name,
            user.scheduleGroup?.name ?? 'No team',
            user.branch?.name,
            user.email,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <div className="mt-1 flex flex-wrap gap-1">
          {user.id === me.id ? <Badge tone="blue">You</Badge> : null}
          {user.archivedAt ? <Badge>Archived</Badge> : null}
          {pending && !user.archivedAt ? <Badge tone="amber">Invited</Badge> : null}
          {user.sm8Uuid ? <Badge tone="purple">Imported</Badge> : null}
          {user.isSchedulable ? <Badge tone="green">On the board</Badge> : null}
        </div>
      </div>

      {can('USER_MANAGE') ? (
        <div className="flex flex-wrap items-center gap-2">
          {pending && !user.archivedAt ? (
            <Button
              size="sm"
              loading={resend.isPending}
              onClick={() => resend.mutate({ userId: user.id })}
            >
              Resend invite
            </Button>
          ) : null}

          {user.archivedAt ? (
            <Button
              size="sm"
              loading={restore.isPending}
              onClick={() => restore.mutate({ id: user.id })}
            >
              Restore
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              disabled={user.id === me.id}
              loading={archive.isPending}
              onClick={() =>
                archive.mutate(
                  { id: user.id },
                  { onSuccess: () => navigate('/settings?tab=people') },
                )
              }
            >
              Archive
            </Button>
          )}
        </div>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */

function dollarsFromCents(cents: number | null | undefined): string {
  if (cents == null) return ''
  return (cents / 100).toFixed(2)
}

function centsFromDollars(value: string): number | null {
  const trimmed = value.trim()
  if (!trimmed) return null
  const amount = Number(trimmed)
  if (!Number.isFinite(amount) || amount < 0) return null
  return Math.round(amount * 100)
}

function homeLine(address: AddressDraft): string | null {
  const street = [address.unit, address.streetNumber, address.street].filter(Boolean).join(' ')
  const line = [street, address.suburb, address.postcode].filter(Boolean).join(', ')
  return line || null
}

function DetailsTab({ userId }: { userId: string }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()

  const detail = useQuery(trpc.users.get.queryOptions({ id: userId }))
  const roles = useQuery(trpc.users.roles.queryOptions())
  const teams = useQuery(trpc.users.scheduleGroups.queryOptions())

  const update = useMutation(
    trpc.users.update.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(trpc.users.pathFilter()),
    }),
  )

  const setTeam = useMutation(
    trpc.users.setScheduleGroup.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries(trpc.users.pathFilter()),
    }),
  )

  const user = detail.data

  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [colour, setColour] = useState('#2167b5')
  const [roleId, setRoleId] = useState('')
  const [isSchedulable, setIsSchedulable] = useState(true)
  const [costRate, setCostRate] = useState('')
  const [chargeRate, setChargeRate] = useState('')
  const [home, setHome] = useState<AddressDraft>(emptyAddress)
  const [homeTouched, setHomeTouched] = useState(false)
  const [homeProblem, setHomeProblem] = useState<string | null>(null)

  useEffect(() => {
    if (!user) return
    setName(user.name)
    setPhone(user.phone ?? '')
    setColour(user.colour)
    setRoleId(user.role.id)
    setIsSchedulable(user.isSchedulable)
    setCostRate(dollarsFromCents(user.costRatePerHour))
    setChargeRate(dollarsFromCents(user.chargeRatePerHour))
    setHome(addressDraftFrom(user.homeAddress))
    setHomeTouched(false)
  }, [user])

  if (detail.isPending) {
    return (
      <Card>
        <CardHeader title="Details" />
        <SkeletonRows rows={5} />
      </Card>
    )
  }

  if (detail.error || !user) {
    return (
      <ErrorState
        message={detail.error ? errorMessage(detail.error) : 'That person was not found.'}
      />
    )
  }

  const errors = fieldErrors(update.error)
  const editable = can('USER_MANAGE')

  const homeIsBlank = !home.street?.trim() && !home.suburb?.trim() && home.latitude == null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setHomeProblem(null)
    if (homeTouched && !homeIsBlank && !addressIsUsable(home)) {
      setHomeProblem('A home address needs at least a street and a suburb.')
      return
    }
    update.mutate({
      id: user.id,
      name,
      phone: phone.trim() || null,
      colour,
      roleId,
      isSchedulable,
      costRatePerHour: centsFromDollars(costRate),
      chargeRatePerHour: centsFromDollars(chargeRate),
      ...(homeTouched ? { homeAddress: homeIsBlank ? null : home } : {}),
    })
  }

  return (
    <>
      <form onSubmit={submit} className="flex flex-col gap-[var(--stack-gap)]" noValidate>
        <Card>
          <CardHeader title="Details" />
          <div className="grid gap-3 p-4 sm:grid-cols-2">
            <Field label="Name" htmlFor="p-name" error={errors['name']} required>
              <Input
                id="p-name"
                value={name}
                disabled={!editable}
                onChange={(event) => setName(event.target.value)}
                required
              />
            </Field>

            <Field
              label="Email"
              htmlFor="p-email"
              hint="Login address. Change via invite or support."
            >
              <Input id="p-email" value={user.email} disabled readOnly />
            </Field>

            <Field label="Phone" htmlFor="p-phone" error={errors['phone']}>
              <Input
                id="p-phone"
                value={phone}
                disabled={!editable}
                onChange={(event) => setPhone(event.target.value)}
              />
            </Field>

            <Field label="Board colour" htmlFor="p-colour" error={errors['colour']}>
              <div className="flex items-center gap-2">
                <input
                  id="p-colour"
                  type="color"
                  value={colour}
                  disabled={!editable}
                  onChange={(event) => setColour(event.target.value)}
                  className="size-11 shrink-0 cursor-pointer rounded-lg border-0 bg-transparent p-1"
                />
                <Input
                  value={colour}
                  disabled={!editable}
                  onChange={(event) => setColour(event.target.value)}
                  pattern="^#[0-9a-fA-F]{6}$"
                  className="font-mono uppercase"
                />
              </div>
            </Field>

            <Field label="Role" htmlFor="p-role">
              <Select
                id="p-role"
                value={roleId}
                disabled={!editable}
                onChange={(event) => setRoleId(event.target.value)}
              >
                {(roles.data ?? []).map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label="Team"
              htmlFor="p-team"
              hint="The section they appear under on the dispatch board."
            >
              <Select
                id="p-team"
                value={user.scheduleGroupId ?? ''}
                disabled={!editable || setTeam.isPending}
                onChange={(event) =>
                  setTeam.mutate({ userId: user.id, scheduleGroupId: event.target.value || null })
                }
              >
                <option value="">No team</option>
                {(teams.data ?? []).map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="On the schedule" htmlFor="p-schedulable">
              <Select
                id="p-schedulable"
                value={isSchedulable ? 'yes' : 'no'}
                disabled={!editable}
                onChange={(event) => setIsSchedulable(event.target.value === 'yes')}
              >
                <option value="yes">Yes — appears on the board</option>
                <option value="no">No — hidden from the board</option>
              </Select>
            </Field>

            <Field
              label="Cost rate / hour"
              htmlFor="p-cost"
              hint="Internal cost in dollars."
              error={errors['costRatePerHour']}
            >
              <Input
                id="p-cost"
                inputMode="decimal"
                placeholder="0.00"
                value={costRate}
                disabled={!editable}
                onChange={(event) => setCostRate(event.target.value)}
              />
            </Field>

            <Field
              label="Charge rate / hour"
              htmlFor="p-charge"
              hint="Default bill-out rate in dollars."
              error={errors['chargeRatePerHour']}
            >
              <Input
                id="p-charge"
                inputMode="decimal"
                placeholder="0.00"
                value={chargeRate}
                disabled={!editable}
                onChange={(event) => setChargeRate(event.target.value)}
              />
            </Field>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Home"
            action={
              editable && !homeIsBlank ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setHome(emptyAddress())
                    setHomeTouched(true)
                  }}
                >
                  Clear
                </Button>
              ) : null
            }
          />
          <div className="flex flex-col gap-3 p-4">
            <p className="text-xs text-ink-500">
              Where their working day starts and ends. The route planner measures the first drive
              of the day from here and the last one back, so the final job lands near home.
            </p>
            {editable ? (
              <AddressField
                id="p-home"
                value={home}
                onChange={(next) => {
                  setHome(next)
                  setHomeTouched(true)
                }}
              />
            ) : (
              <p className="text-sm text-ink-900">{homeLine(home) ?? 'Not set'}</p>
            )}
            {homeProblem ? (
              <p role="alert" className="text-xs text-red-600">
                {homeProblem}
              </p>
            ) : null}
            <div className="text-xs text-ink-500">
              <span className="font-medium text-ink-700">Covers: </span>
              {user.serviceAreas.length > 0
                ? user.serviceAreas.map((entry) => entry.area.name).join(', ')
                : 'no service area yet'}
              {can('SETTINGS_MANAGE') ? (
                <>
                  {' · '}
                  <Link
                    to="/settings?tab=areas"
                    className="font-medium text-brand-700 hover:underline"
                  >
                    Service areas
                  </Link>
                </>
              ) : null}
            </div>
          </div>

          {editable ? (
            <div className="flex items-center justify-end gap-2 border-t border-ink-200 px-[var(--row-pad-x)] py-2">
              {update.error ? (
                <p className="mr-auto text-xs text-red-600">{errorMessage(update.error)}</p>
              ) : update.isSuccess ? (
                <p className="mr-auto text-xs text-emerald-700">Saved.</p>
              ) : null}
              <Button type="submit" variant="primary" size="sm" loading={update.isPending}>
                Save changes
              </Button>
            </div>
          ) : null}
        </Card>
      </form>

      <Card>
        <CardHeader title="Account" />
        <dl className="grid gap-2 p-4 text-xs sm:grid-cols-2">
          <Detail label="Last seen">
            {user.lastSeenAt ? relativeTime(user.lastSeenAt) : 'Never signed in'}
          </Detail>
          <Detail label="Account created">{formatDateTime(user.createdAt)}</Detail>
          <Detail label="Email verified">{user.emailVerified ? 'Yes' : 'No'}</Detail>
          <Detail label="Cost / charge">
            {user.costRatePerHour != null || user.chargeRatePerHour != null
              ? `${money(user.costRatePerHour)} / ${money(user.chargeRatePerHour)}`
              : '—'}
          </Detail>
        </dl>

        {user.role.permissions.length > 0 ? (
          <div className="border-t border-ink-200 p-4">
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-500 uppercase">
              Role permissions
            </h3>
            <div className="flex flex-wrap gap-1">
              {user.role.permissions.map((permission) => (
                <Badge key={permission}>{humanise(permission)}</Badge>
              ))}
            </div>
          </div>
        ) : null}
      </Card>

      <Sm8Card user={user} />
    </>
  )
}

/* -------------------------------------------------------------------------- */

/** Preferred order for ServiceM8 staff fields; anything else follows alphabetically. */
const SM8_FIELD_ORDER = [
  'uuid',
  'first',
  'last',
  'email',
  'mobile',
  'job_title',
  'color',
  'active',
  'hide_from_schedule',
  'security_role_uuid',
  'labour_material_uuid',
  'customfield_licence_number',
  'can_receive_push_notification',
  'status_message',
  'status_message_timestamp',
  'custom_icon_url',
  'lat',
  'lng',
  'geo_timestamp',
  'navigating_to_job_uuid',
  'navigating_timestamp',
  'navigating_expiry_timestamp',
  'edit_date',
] as const

const SM8_FIELD_LABELS: Record<string, string> = {
  uuid: 'ServiceM8 UUID',
  first: 'First name',
  last: 'Last name',
  email: 'Email',
  mobile: 'Mobile',
  job_title: 'Job title',
  color: 'Colour',
  active: 'Active',
  hide_from_schedule: 'Hide from schedule',
  security_role_uuid: 'Security role UUID',
  labour_material_uuid: 'Labour material UUID',
  customfield_licence_number: 'Licence number',
  can_receive_push_notification: 'Push notifications',
  status_message: 'Status message',
  status_message_timestamp: 'Status message at',
  custom_icon_url: 'Custom icon URL',
  lat: 'Latitude',
  lng: 'Longitude',
  geo_timestamp: 'Location at',
  navigating_to_job_uuid: 'Navigating to job',
  navigating_timestamp: 'Navigation started',
  navigating_expiry_timestamp: 'Navigation expires',
  edit_date: 'Last edited in ServiceM8',
}

function formatSm8Value(value: unknown): string {
  if (value == null) return '—'
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || trimmed.startsWith('0000-00-00')) return '—'
    return trimmed
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}

function orderedSm8Keys(payload: Record<string, unknown>): string[] {
  const remaining = new Set(Object.keys(payload))
  const ordered: string[] = []

  for (const key of SM8_FIELD_ORDER) {
    if (remaining.has(key)) {
      ordered.push(key)
      remaining.delete(key)
    }
  }

  return [...ordered, ...[...remaining].sort()]
}

function Sm8Card({ user }: { user: NonNullable<UserDetail> }) {
  if (!user.sm8Source) {
    return (
      <Card>
        <CardHeader title="Imported from ServiceM8" />
        <p className="p-4 text-xs text-ink-500">
          {user.sm8Uuid
            ? `Linked to ServiceM8 (${user.sm8Uuid}) but the staged staff record is no longer in the database.`
            : `Created in ${BRAND.name} — not imported from ServiceM8.`}
        </p>
      </Card>
    )
  }

  const source = user.sm8Source

  return (
    <Card>
      <CardHeader title="Imported from ServiceM8" />
      <p className="px-4 pt-3 text-xs text-ink-500">
        Verbatim staff record. {BRAND.name} keeps this so you can see what came across, including
        fields that are not editable here. The licence number ServiceM8 carried is a single free
        text field, which is the reason the Certifications tab exists.
      </p>
      <dl className="divide-y divide-ink-100 p-4">
        <Sm8Row label="Source active">{source.sourceActive ? 'Yes' : 'No'}</Sm8Row>
        <Sm8Row label="Fetched">{formatDateTime(source.fetchedAt)}</Sm8Row>
        <Sm8Row label="Loaded">
          {source.loadedAt ? formatDateTime(source.loadedAt) : 'Not yet'}
        </Sm8Row>
        {orderedSm8Keys(source.payload).map((key) => (
          <Sm8Row key={key} label={SM8_FIELD_LABELS[key] ?? humanise(key.toUpperCase())}>
            {formatSm8Value(source.payload[key])}
          </Sm8Row>
        ))}
      </dl>
    </Card>
  )
}

function Sm8Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 py-2 sm:grid-cols-[12rem_1fr] sm:gap-3">
      <dt className="text-xs text-ink-500">{label}</dt>
      <dd className="break-all text-xs text-ink-900">{children}</dd>
    </div>
  )
}

/* -------------------------------------------------------------------------- */

/**
 * Where this person sits on the board, and what they are away for.
 *
 * Read-only. Editing leave belongs on the board, where the surrounding week is
 * visible — a date picker with no context is how somebody gets booked over their
 * own annual leave.
 */
function ScheduleTab({ userId }: { userId: string }) {
  const trpc = useTRPC()
  const detail = useQuery(trpc.users.get.queryOptions({ id: userId }))
  const user = detail.data

  return (
    <Card>
      <CardHeader
        title="Schedule"
        action={
          <Link to="/board" className="text-xs font-medium text-brand-700 hover:underline">
            Open the board
          </Link>
        }
      />
      {detail.isPending ? (
        <SkeletonRows rows={3} />
      ) : !user ? (
        <ErrorState message="That person was not found." />
      ) : (
        <dl className="grid gap-2 p-4 text-xs sm:grid-cols-2">
          <Detail label="On the board">{user.isSchedulable ? 'Yes' : 'No'}</Detail>
          <Detail label="Team">{user.scheduleGroup?.name ?? 'No team'}</Detail>
          <Detail label="Branch">{user.branch?.name ?? '—'}</Detail>
          <Detail label="Board colour">
            <span className="inline-flex items-center gap-1.5">
              <span
                aria-hidden
                className="size-3 rounded-sm ring-1 ring-ink-200"
                style={{ backgroundColor: user.colour }}
              />
              <span className="font-mono uppercase">{user.colour}</span>
            </span>
          </Detail>
        </dl>
      )}
      <p className="border-t border-ink-200 px-[var(--row-pad-x)] py-2 text-xs text-ink-500">
        Leave and bookings are set on the dispatch board, where the surrounding week is visible.
      </p>
    </Card>
  )
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-ink-500">{label}</dt>
      <dd className="font-medium text-ink-900">{children}</dd>
    </div>
  )
}
